import { useCallback } from 'react';

import { Select } from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';

import type { VisibilityState } from '@tanstack/react-table';
import type { SelectItemOption, SelectOption } from '@kc-console/common-ui';
import type { DataTableColumnContent } from '../DataTable/types';

export interface ActionBarSettingColumnSelectProps {
  columns: DataTableColumnContent<unknown>[];
  onColumnOrderChange: (columnOrder: string[]) => void;
  onColumnVisibilityChange: (columnVisibility: VisibilityState) => void;
}

export default function ActionBarSettingColumnSelect(props: ActionBarSettingColumnSelectProps) {
  const { t } = useTranslation();
  const { columns, onColumnOrderChange, onColumnVisibilityChange } = props;

  const controllableColumns = columns.filter(col => !col.disableHiding);
  const columnSelectOptions = controllableColumns.map(col => ({
    value: col.id,
    text: col.headerName,
    disabled: col.visibleOnly,
  }));
  const visibleColumns = controllableColumns.filter(col => col.isVisible).map(col => col.id);

  const handleVisibleColumnsChange = useCallback(
    (newVisibleColumns: string[]) => {
      onColumnVisibilityChange?.(
        columns.reduce((acc, column) => {
          if (
            !newVisibleColumns.includes(column.id) &&
            (!column.disableHiding || !column.defaultIsVisible)
          ) {
            acc[column.id] = false;
          }
          return acc;
        }, {} as VisibilityState),
      );
    },
    [columns, onColumnVisibilityChange],
  );

  const handleColumnsReorder = (options: SelectOption<string, {}>[]) => {
    onColumnOrderChange(
      options
        .filter((option): option is SelectItemOption<string, {}> => option.type !== 'section')
        .map(option => option.value),
    );
  };

  return (
    <Select
      size="md"
      multiple
      confirmable
      enableReordering
      options={columnSelectOptions}
      value={visibleColumns}
      onChange={handleVisibleColumnsChange}
      onOptionsReorder={handleColumnsReorder}
      renderValue={value =>
        t('ui:column.edit_label', {
          selectedText: value.length
            ? t('common:common.label.selected_items', { count: value.length })
            : t('ui:column.select_none'),
        })
      }
      slotProps={{
        footer: {
          applyLabel: t('ui:action.apply'),
          cancelLabel: t('common:common.action.cancel'),
        },
      }}
    />
  );
}
